/**
 * How many people are waiting on an answer to a coaching request, or `null` when nobody can say.
 *
 * ── Why this is a hook in `entities/` rather than five lines in each screen ──────────────────
 * It WAS five lines inside `pages/client-roster/lib/useClientRoster`, which is where the roster's
 * archive badge got its number. The navigation panel became the second surface to need it on
 * 17 September 2026, and the panel lives in `app/` — which may reach `entities/client`'s barrel but
 * may not reach into a page slice's `lib/`. Copying the fold would have created two numbers that
 * can disagree about the same fact, on the same screen, four hundred pixels apart. The repo's
 * second-consumer rule and the shape of the bug point the same way.
 *
 * ── `null` is not zero, and the difference is the whole contract ─────────────────────────────
 * `null` means the read is in flight or it failed. Zero means nobody is waiting. A badge drawn from
 * a collapsed "falsy" is a claim that nobody is waiting, made at the moment the app does not know,
 * and saying that wrongly is the one failure a waiting-list badge exists to prevent. Every caller
 * therefore draws its badge only for a count strictly above zero, and draws nothing at all for
 * `null`.
 *
 * ── It is a count of what is LOADED ──────────────────────────────────────────────────────────
 * `CoachingRequestPageResponse` carries `hasMore` and a cursor pair and nothing that totals the
 * list, so there is no total to read. Only the first page is fetched; nothing here calls
 * `fetchNextPage`. For a trainer holding fewer requests than a page — which the seat cap makes
 * every trainer — loaded and total are the same number. The archive screen's own badge says the
 * same thing for the same reason.
 *
 * ── It costs no extra request where one is already in flight ─────────────────────────────────
 * These are the SAME query options the roster and the archive pass, so TanStack Query serves all
 * three readers from one cache entry. On a screen that already reads coaching requests the panel
 * adds nothing; elsewhere it adds one keyset page with no plan or session data on it.
 */

import { useInfiniteQuery } from '@tanstack/react-query';

import { coachingRequestsQueryOptions } from '../api/clientQueries';
import { coachingRequests } from '../model/coachingInbox';

export function useCoachingRequestCount(): number | null {
  const requests = useInfiniteQuery(coachingRequestsQueryOptions());

  return requests.isSuccess ? coachingRequests(requests.data.pages).rows.length : null;
}
